/**
 * @file useUrlRegistry.ts
 * @description Данные реестра, чьё состояние живёт в адресе страницы
 * (phase19.31, зона «Клиенты и сделки»; тот же порядок, что у реестра
 * заказов): фильтры и страница — в адресе, хук только читает его и ходит на
 * сервер постранично.
 *
 * Перечитывание тихое: прежний список остаётся на экране, пока идёт новый
 * запрос; ошибка при уже показанном списке список не стирает — над ним
 * встаёт полоса «данные могут быть устаревшими». Гонку ответов (быстро
 * листали страницы) гасит номер запроса.
 *
 * Функции разбора/записи/загрузки передаются стабильными (объявлены на
 * уровне модуля страницы) — иначе запрос перезапускался бы на каждый рендер.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import { ApiError } from "../../lib/api-client";
import { patchListQuery } from "../../lib/crm-list-query";

export interface UrlRegistrySpec<Q extends { page: number }, R> {
  parse: (params: URLSearchParams) => Q;
  write: (query: Q) => URLSearchParams;
  load: (query: Q) => Promise<{ items: R[]; total: number }>;
  reset: (query: Q) => Q;
  /** Тексты ошибки: без списка и со списком на экране. */
  errorText: { empty: string; stale: string };
}

export interface UrlRegistryState<Q, R> {
  query: Q;
  setQuery: (patch: Partial<Q>) => void;
  resetFilters: () => void;
  items: R[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useUrlRegistry<Q extends { page: number }, R>(spec: UrlRegistrySpec<Q, R>): UrlRegistryState<Q, R> {
  const { parse, write, load, reset, errorText } = spec;
  const [params, setParams] = useSearchParams();
  // Ключ — нормализованный адрес: новый объект состояния не перезапускает запрос зря.
  const key = write(parse(params)).toString();
  const query = parse(new URLSearchParams(key));
  const [items, setItems] = useState<R[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const requestId = useRef(0);

  useEffect(() => {
    const id = ++requestId.current;
    setLoading(true);
    load(parse(new URLSearchParams(key)))
      .then((res) => {
        if (id !== requestId.current) return;
        setItems(res.items);
        setTotal(res.total);
        setFailed(false);
      })
      .catch((err: unknown) => {
        if (id !== requestId.current) return;
        // Причину — в консоль разработчика, человеку — понятный текст.
        if (!(err instanceof ApiError) && import.meta.env.DEV) console.warn("useUrlRegistry", err);
        setFailed(true);
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
  }, [key, attempt, load, parse]);

  const setQuery = useCallback(
    (patch: Partial<Q>) => {
      setParams((prev) => write(patchListQuery(parse(prev), patch)), { replace: true });
    },
    [setParams, write, parse],
  );

  const resetFilters = useCallback(() => {
    setParams((prev) => write(reset(parse(prev))), { replace: true });
  }, [setParams, write, parse, reset]);

  const refresh = useCallback(() => setAttempt((n) => n + 1), []);

  return {
    query,
    setQuery,
    resetFilters,
    items,
    total,
    loading,
    error: failed ? (items.length > 0 ? errorText.stale : errorText.empty) : null,
    refresh,
  };
}
